import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';

/** Purpose: edit the tenant's branded login experience. Why: customer-facing authentication settings must be scoped to the selected bot. */
export default function TenantLoginEditor({ botId }) {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    axios.get(`/api/v1/admin/bots/${botId}/login-accounts`).then(({ data }) => {
      if (!active) return;
      const items = data.accounts || [];
      setAccounts(items);
      if (items.length === 1) { setAccountId(items[0].id); setEmail(items[0].email); }
    }).catch((err) => { if (active) setError(err.response?.data?.error || 'Unable to load tenant accounts.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [botId, retry]);
  /** Purpose: Validate and submit the selected account's login edits. Why: Invalid email and mismatched replacement passwords must be caught before submission. */
  const save = async () => {
    setError(''); setSuccess('');
    if (!accountId || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Select an account and enter a valid email.'); return; }
    if (password && (password.length < 8 || password !== confirm)) { setError('Use at least 8 characters and make sure both passwords match.'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`/api/v1/admin/bots/${botId}/login-accounts/${accountId}`, { email: email.trim().toLowerCase(), ...(password ? { password } : {}) });
      setAccounts((items) => items.map((item) => item.id === accountId ? data.account : item));
      setEmail(data.account.email); setPassword(''); setConfirm('');
      setSuccess('Tenant login saved. Use the updated details to sign in.');
    } catch (err) { setError(err.response?.data?.error || 'Unable to update tenant login.'); }
    finally { setSaving(false); }
  };
  return <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
    <Typography variant="h6">Tenant login</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Only Super Admin can update these details. Changes apply to this account across all its workspaces and sign out existing sessions.</Typography>
    {loading ? <CircularProgress size={22} aria-label="Loading tenant accounts" /> : <>
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}{!accounts.length && <Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>}</Alert>}
      {success && <Alert severity="success" sx={{ my: 1 }}>{success}</Alert>}
      {!accounts.length && !error ? <Alert severity="info">No tenant admin account is assigned to this bot. Assign one from Admin Panel first.</Alert> : accounts.length > 0 && <>
        <TextField select fullWidth label="Tenant account" value={accountId} disabled={saving} margin="normal" onChange={(event) => {
          const id = event.target.value; setAccountId(id); setEmail(accounts.find((item) => item.id === id)?.email || ''); setPassword(''); setConfirm(''); setError(''); setSuccess('');
        }}>{accounts.map((item) => <MenuItem key={item.id} value={item.id}>{item.name} — {item.email}</MenuItem>)}</TextField>
        <TextField fullWidth label="Login email" type="email" value={email} disabled={saving || !accountId} onChange={(event) => setEmail(event.target.value)} margin="normal" autoComplete="off" />
        <TextField fullWidth label="New password" type="password" value={password} disabled={saving || !accountId} onChange={(event) => setPassword(event.target.value)} margin="normal" autoComplete="new-password" helperText="Leave blank to keep the current password. Minimum 8 characters." />
        <TextField fullWidth label="Confirm new password" type="password" value={confirm} disabled={saving || !accountId} onChange={(event) => setConfirm(event.target.value)} margin="normal" autoComplete="new-password" />
        <Button variant="outlined" disabled={saving || !accountId} onClick={save} sx={{ my: 1 }}>{saving ? 'Saving login…' : 'Save tenant login'}</Button>
      </>}
    </>}
  </Box>;
}
